import { badgeService, BadgeDefinitionAPI } from './badgeService';
import { transformBadgeDefinitionFromAPI, transformBadgeDefinitionToAPI } from '../utils/badgeTransformers';
import { capitalize } from '../utils/stringUtils';

export interface BadgeLevelForm {
  badgeLevelId?: number;
  name: string;
  threshold: number;
  image: string;
}

export interface BadgeFormData {
  id?: string;
  name: string;
  slug: string;
  category: string;
  description: string;
  badgeImageUrl: string;
  levels: BadgeLevelForm[];
  color: string;
}

export const adminBadgeService = {
  async getAllBadges(): Promise<BadgeFormData[]> {
    // Note: caching is handled internally by axios
    const badges = await badgeService.getBadges();
    return badges.map(badge => {
      const transformed = transformBadgeDefinitionFromAPI(badge);
      const levels = (badge as any).badgeLevels
        ? (badge as any).badgeLevels
            .slice()
            .sort((a: any, b: any) => a.levelPointsRequired - b.levelPointsRequired)
            .map((level: any) => ({
              badgeLevelId: level.badgeLevelId,
              name: level.levelName?.toLowerCase() || '',
              threshold: level.levelPointsRequired,
              image: level.levelImageUrl || ''
            }))
        : Object.entries(transformed.levelThresholds)
            .sort((a, b) => a[1] - b[1])
            .map(([name, threshold]) => ({
              name,
              threshold,
              image: transformed.levelImages[name]
            }));
      return {
        id: transformed.id,
        name: transformed.name,
        slug: transformed.slug,
        category: transformed.category,
        description: transformed.description,
        badgeImageUrl: transformed.badgeImageUrl || '',
        levels,
        color: transformed.color
      };
    });
  },
  async createBadge(badgeData: BadgeFormData): Promise<BadgeDefinitionAPI> {
    const badgeDef = {
      name: badgeData.name,
      slug: badgeData.slug,
      description: badgeData.description,
      category: badgeData.category,
      badgeImageUrl: badgeData.badgeImageUrl,
      levelImages: Object.fromEntries(badgeData.levels.map(l => [l.name, l.image])),
      levelThresholds: Object.fromEntries(badgeData.levels.map(l => [l.name, l.threshold])),
      color: badgeData.color
    } as any;
    const apiData = transformBadgeDefinitionToAPI(badgeDef);
    return await badgeService.createBadge(apiData as any);
  },

  async updateBadge(badgeId: string, badgeData: BadgeFormData): Promise<void> {
    const sortedLevels = [...badgeData.levels].sort((a, b) => a.threshold - b.threshold);

    const apiPayload = {
      id: badgeId,
      name: badgeData.name,
      slug: badgeData.slug,
      description: badgeData.description,
      category: badgeData.category,
      badgeImageUrl: badgeData.badgeImageUrl,
      color: badgeData.color,
      badgeId,
      badgeLevels: sortedLevels.map((level, index) => ({
        badgeLevelId: level.badgeLevelId,
        badgeId,
        levelName: capitalize(level.name),
        levelNumber: index + 1,
        levelDescription: `${capitalize(level.name)} badge` + (index === 0 ? ' for beginners' : ''),
        levelImageUrl: level.image,
        levelPointsRequired: level.threshold
      })),
      levelImages: Object.fromEntries(badgeData.levels.map(l => [l.name, l.image])),
      levelThresholds: Object.fromEntries(badgeData.levels.map(l => [l.name, l.threshold]))
    } as any;

    const apiData = transformBadgeDefinitionToAPI(apiPayload);
    await badgeService.updateBadge(badgeId, apiData as any);
  },

  async deleteBadge(badgeId: string): Promise<void> {
    await badgeService.deleteBadge(badgeId);
  }
};
